import { isAcceptanceTest } from 'hub-http/helpers/env';
import { useEffect, useMemo } from 'react';
import { getRandomizedIndices } from 'academy-common-lib/utils/commonUtils';
import { useAsyncBulkGetQuizMeQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import { useLessonPreQuizSession } from './useLessonPreQuiz.migrated';
import { useLessonQuizMeVideoRequestParams } from '../../interactiveEducation/hooks/useLessonQuizMe.migrated';
import { formatSecondsToTimestamp } from '../../interactiveEducation/utils/contentUtils.migrated';
import I18n from 'I18n';
const QUESTIONS_PER_QUIZ = 2;
export const usePreQuizFromCache = ({
  lessonId,
  contents
}) => {
  const {
    requestParams
  } = useLessonQuizMeVideoRequestParams({
    contents
  });
  const {
    getPreQuizStatus,
    getLessonPreQuizState,
    setPreQuizTotalQuestions,
    getShuffledSelections,
    setShuffledSelections
  } = useLessonPreQuizSession();
  const generationStatus = getPreQuizStatus(lessonId);
  const preQuizState = getLessonPreQuizState(lessonId);
  const shuffledSelections = getShuffledSelections(lessonId);
  const taskIds = useMemo(() => (requestParams || []).reduce((acc, {
    lessonContentId: id
  }) => {
    const task = preQuizState === null || preQuizState === void 0 ? void 0 : preQuizState.tasks[id];
    if (task) {
      acc.push(task);
    }
    return acc;
  }, []), [requestParams, preQuizState === null || preQuizState === void 0 ? void 0 : preQuizState.tasks]);
  const taskIdToContentId = useMemo(() => Object.entries((preQuizState === null || preQuizState === void 0 ? void 0 : preQuizState.tasks) || {}).reduce((map, [contentId, taskId]) => {
    map[taskId] = Number(contentId);
    return map;
  }, {}), [preQuizState === null || preQuizState === void 0 ? void 0 : preQuizState.tasks]);
  const {
    data
  } = useAsyncBulkGetQuizMeQuery({
    variables: {
      taskIds
    },
    fetchPolicy: 'cache-only',
    skip: taskIds.length === 0 || isAcceptanceTest()
  });
  const allTasksCompleted = useMemo(() => {
    var _data$tasks$every, _data$tasks;
    return (_data$tasks$every = data === null || data === void 0 || (_data$tasks = data.tasks) === null || _data$tasks === void 0 ? void 0 : _data$tasks.every(task => task.status === 'COMPLETED')) !== null && _data$tasks$every !== void 0 ? _data$tasks$every : false;
  }, [data]);
  useEffect(() => {
    if (!allTasksCompleted || shuffledSelections || !(data !== null && data !== void 0 && data.tasks)) {
      return;
    }
    const selections = Object.fromEntries(data.tasks.map(task => {
      var _task$quizResponse;
      const questions = ((_task$quizResponse = task.quizResponse) === null || _task$quizResponse === void 0 || (_task$quizResponse = _task$quizResponse.quiz) === null || _task$quizResponse === void 0 ? void 0 : _task$quizResponse.questions) || [];
      const questionIndices = getRandomizedIndices(questions.length, QUESTIONS_PER_QUIZ);
      const answerIndices = questionIndices.map(qi => getRandomizedIndices(questions[qi].answers.length, questions[qi].answers.length));
      return [task.taskId, {
        questionIndices,
        answerIndices
      }];
    }));
    setShuffledSelections({
      lessonId,
      shuffledSelections: selections
    });
  }, [allTasksCompleted, shuffledSelections, data, lessonId, setShuffledSelections]);
  const contentIdNameMap = useMemo(() => (contents || []).reduce((prev, curr) => {
    prev[curr.id] = curr.name;
    return prev;
  }, {}), [contents]);
  const questions = useMemo(() => {
    if (!(data !== null && data !== void 0 && data.tasks) || !shuffledSelections) {
      return [];
    }
    return data.tasks.flatMap(task => {
      var _task$quizResponse2;
      const selection = shuffledSelections[task.taskId];
      if (!selection) {
        return [];
      }
      const lessonContentId = taskIdToContentId[task.taskId];
      const individualQuizQuestions = ((_task$quizResponse2 = task.quizResponse) === null || _task$quizResponse2 === void 0 || (_task$quizResponse2 = _task$quizResponse2.quiz) === null || _task$quizResponse2 === void 0 ? void 0 : _task$quizResponse2.questions) || [];
      return selection.questionIndices.map((questionIndex, i) => {
        var _task$quizResponse3;
        const question = individualQuizQuestions[questionIndex];
        const contentName = lessonContentId !== undefined ? contentIdNameMap[lessonContentId] : undefined;
        return {
          questionTitle: question.questionTitle,
          answers: selection.answerIndices[i].map(ai => question.answers[ai]),
          correlationId: (_task$quizResponse3 = task.quizResponse) === null || _task$quizResponse3 === void 0 ? void 0 : _task$quizResponse3.correlationId,
          lessonContentId,
          timestampSeconds: question.timestampSeconds,
          timestampLabel: contentName && question.timestampSeconds !== undefined ? `${contentName} (${I18n.text('learningCenter.interactiveEducation.quiz.rewatchAt', {
            timestamp: formatSecondsToTimestamp(question.timestampSeconds)
          })})` : contentName
        };
      });
    });
  }, [data, shuffledSelections, taskIdToContentId, contentIdNameMap]);
  useEffect(() => {
    if (questions.length > 0) {
      setPreQuizTotalQuestions({
        lessonId,
        totalQuestions: questions.length
      });
    }
  }, [questions.length, lessonId, setPreQuizTotalQuestions]);
  return {
    questions,
    generationStatus,
    hasQuestions: questions.length > 0
  };
};